import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router';

import { apiGet, apiPost, setCsrfToken } from './client';
import type { AuthEmailAccepted, AuthState, SignupRequest } from './types';
import type { PasskeyOptionsResponse } from './types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  login: (password: string, email?: string, mfaCode?: string) => Promise<void>;
  passkeyLogin: () => Promise<void>;
  signup: (payload: SignupRequest) => Promise<AuthEmailAccepted>;
  verifySignup: (token: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<AuthEmailAccepted>;
  confirmPasswordReset: (token: string, password: string) => Promise<void>;
  acceptInvitation: (token: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Гасит сессию локально — вызывается перехватчиком 401 из QueryProvider. */
  expire: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyState(state: AuthState): AuthStatus {
  setCsrfToken(state.csrf_token ?? null);
  return state.authenticated ? 'authenticated' : 'anonymous';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const epoch = useRef(0);
  const active = useRef(true);

  useEffect(() => {
    let cancelled = false;
    active.current = true;
    const attempt = ++epoch.current;
    apiGet<AuthState>('/api/auth/me')
      .then((state) => {
        if (!cancelled && attempt === epoch.current) setStatus(applyState(state));
      })
      .catch(() => {
        // 401 на /api/auth/me — нормальный ответ «сессии нет», не ошибка
        if (!cancelled && attempt === epoch.current) {
          setCsrfToken(null);
          setStatus('anonymous');
        }
      });
    return () => {
      cancelled = true;
      active.current = false;
      epoch.current += 1;
    };
  }, []);

  const refreshAuth = useCallback(async (attempt: number) => {
    if (!active.current || attempt !== epoch.current) return;
    // Only the current /me response supplies local state and CSRF. A later
    // logout/expiry/auth operation invalidates pending responses, not cookies
    // or server-side actions that may already have completed.
    const state = await apiGet<AuthState>('/api/auth/me');
    if (active.current && attempt === epoch.current) setStatus(applyState(state));
  }, []);

  const login = useCallback(async (password: string, email?: string, mfaCode?: string) => {
    if (!active.current) return;
    const attempt = ++epoch.current;
    await apiPost<AuthState>('/api/auth/login', {
      password,
      ...(email ? { email } : {}),
      ...(mfaCode ? { mfa_code: mfaCode } : {}),
    });
    // Источник истины по CSRF — только /api/auth/me (Д-Ф3)
    await refreshAuth(attempt);
  }, [refreshAuth]);

  const passkeyLogin = useCallback(async () => {
    if (!active.current) return;
    const attempt = ++epoch.current;
    const options = await apiPost<PasskeyOptionsResponse>('/api/auth/passkeys/options');
    const { getPasskey } = await import('./passkeys');
    if (!active.current || attempt !== epoch.current) return;
    const credential = await getPasskey(options.public_key);
    if (!active.current || attempt !== epoch.current) return;
    await apiPost<AuthState>('/api/auth/passkeys/verify', {
      challenge_id: options.challenge_id,
      credential,
    });
    await refreshAuth(attempt);
  }, [refreshAuth]);

  const acceptInvitation = useCallback(async (token: string, password: string, displayName?: string) => {
    if (!active.current) return;
    const attempt = ++epoch.current;
    await apiPost<AuthState>('/api/auth/invitations/accept', {
      token,
      password,
      ...(displayName ? { display_name: displayName } : {}),
    });
    await refreshAuth(attempt);
  }, [refreshAuth]);

  const signup = useCallback((payload: SignupRequest) => (
    apiPost<AuthEmailAccepted>('/api/auth/signup', payload)
  ), []);

  const verifySignup = useCallback(async (token: string) => {
    if (!active.current) return;
    const attempt = ++epoch.current;
    await apiPost<AuthState>('/api/auth/signup/verify', { token });
    await refreshAuth(attempt);
  }, [refreshAuth]);

  const requestPasswordReset = useCallback((email: string) => (
    apiPost<AuthEmailAccepted>('/api/auth/password-reset/request', { email })
  ), []);

  const confirmPasswordReset = useCallback(async (token: string, password: string) => {
    if (!active.current) return;
    const attempt = ++epoch.current;
    await apiPost<AuthState>('/api/auth/password-reset/confirm', { token, password });
    await refreshAuth(attempt);
  }, [refreshAuth]);

  const logout = useCallback(async () => {
    if (!active.current) return;
    const attempt = ++epoch.current;
    try {
      await apiPost<Record<string, never>>('/api/auth/logout');
    } finally {
      if (active.current && attempt === epoch.current) {
        setCsrfToken(null);
        setStatus('anonymous');
      }
    }
  }, []);

  const expire = useCallback(() => {
    if (!active.current) return;
    epoch.current += 1;
    setCsrfToken(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, login, passkeyLogin, signup, verifySignup, requestPasswordReset, confirmPasswordReset, acceptInvitation, logout, expire }),
    [status, login, passkeyLogin, signup, verifySignup, requestPasswordReset, confirmPasswordReset, acceptInvitation, logout, expire],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth вызван вне AuthProvider');
  return value;
}

/**
 * Д-Ф4: при отсутствии сессии уводим на вход и запоминаем исходный маршрут.
 * Владелец открывает карточку поездки по ссылке из Telegram — после входа
 * он обязан попасть именно туда, а не на главную.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div role="status" className="p-8 text-ink-dim">
        проверяем сессию…
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
